import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./app-theme.css";
import { I18nProvider } from "@/i18n";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { PlanPreviewProvider } from "@/context/PlanPreviewContext";
import { ScopeProvider } from "@/context/ScopeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import AuthCallback from "@/pages/AuthCallback";

import Landing from "@/pages/Landing";
import DemoLanding from "@/pages/DemoLanding";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Movements from "@/pages/Movements";
import Categories from "@/pages/Categories";
import Contracts from "@/pages/Contracts";
import Charts from "@/pages/Charts";
import Exports from "@/pages/Exports";
import Settings from "@/pages/Settings";
import Subscription from "@/pages/Subscription";
import Import from "@/pages/Import";
import Affiliates from "@/pages/Affiliates";
import AffiliateProgram from "@/pages/AffiliateProgram";
import PaymentResult from "@/pages/PaymentResult";
import UniversalImport from "@/pages/UniversalImport";
import Capture from "@/pages/Capture";
import UploadChooser from "@/pages/UploadChooser";
import Admin from "@/pages/Admin";
import Advisor from "@/pages/Advisor";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { captureRefFromUrl } from "@/lib/ref";
import { useEffect } from "react";

function AdminGate({ children }) {
  const { user } = useAuth();
  const staff = ["admin", "support", "finance"];
  if (user && !staff.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Captura el ?ref= de afiliado en el PRIMER aterrizaje, sea cual sea la ruta
  // (landing, /register, etc.). Cuenta el clic y guarda el ref para el alta.
  useEffect(() => { captureRefFromUrl(); }, [location.search]);
  // Procesa el retorno de Google (session_id en el fragmento) antes que cualquier ruta protegida.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/afiliados" element={<AffiliateProgram />} />
      <Route path="/payment/success" element={<PaymentResult status="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult status="cancel" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        element={
          <ProtectedRoute>
            <ScopeProvider>
              <AppLayout />
            </ScopeProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/movements" element={<Movements />} />
        <Route path="/import" element={<Import />} />
        <Route path="/import/universal" element={<UniversalImport />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/charts" element={<Charts />} />
        <Route path="/exports" element={<Exports />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/affiliates" element={<Affiliates />} />
        <Route path="/advisor" element={<Advisor />} />
        <Route path="/capture" element={<Capture />} />
        <Route path="/upload" element={<UploadChooser />} />
        <Route path="/admin" element={<AdminGate><Admin /></AdminGate>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <PlanPreviewProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
            <Toaster position="bottom-right" richColors closeButton />
          </PlanPreviewProvider>
        </AuthProvider>
      </I18nProvider>
      </ThemeProvider>
    </div>
  );
}
